import {
  HistoryOutlined,
  MoreOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, MenuProps, message, Modal, Space, Spin } from 'antd';
import { EinvoiceHistoryApis } from 'apis/EinvoiceHistoryApis';
import TableComponent from 'components/Table/TableComponent';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import DetailEinvoiceHistoryModal from 'pages/Accountant/EinvoiceHistory/DetailEinvoiceHistoryModal';
import React, { useEffect, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { formatCurrency } from 'utils/constants/functions';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  sumPaymentId: string;
  soCodes?: string[];
  customerInfo?: any;
}

const PaymentSummaryEinvoiceHistoryModal: React.FC<IProps> = ({
  isOpen,
  onClose,
  sumPaymentId,
  soCodes = [],
  customerInfo,
}) => {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState<{
    items: any[];
    total: number;
    currentPage: number;
    pageSize: number;
  }>({
    items: [],
    total: 0,
    currentPage: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);
  const [resending, setResending] = useState(false);

  const isAccountant = !!user?.isAccountant;

  const fetchHistory = async (page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
    if (!sumPaymentId && (!soCodes || soCodes.length === 0)) return;
    try {
      setLoading(true);
      const params: any = {
        limit: pageSize,
        page: page,
      };
      if (sumPaymentId) {
        params.sumPaymentId = sumPaymentId;
      }
      if (soCodes && soCodes.length > 0) {
        params.soCodes = soCodes;
      }
      const res: any = await EinvoiceHistoryApis.getEinvoiceSendHistory(params);
      const items = res?.items || res?.data?.items || res?.data || [];
      const total = res?.total ?? res?.data?.total ?? items.length;
      setHistoryData({
        items: Array.isArray(items) ? items : [],
        total: total || 0,
        currentPage: page,
        pageSize: pageSize,
      });
    } catch (err: any) {
      message.error(err?.message || 'Không thể tải lịch sử gửi HĐĐT');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchHistory(1, DEFAULT_PAGE_SIZE);
    }
  }, [isOpen, sumPaymentId]);

  const checkCanResend = (record: any) => {
    const isEmailValid =
      record?.emailSuccessCount === 0 && (record?.emailResendCount || 0) < 3;
    const isSmsValid =
      record?.smsSuccessCount === 0 && (record?.smsResendCount || 0) < 3;

    const canResendEmail =
      isAccountant || (record?.canResendEmail ?? isEmailValid);
    const canResendSms = isAccountant || (record?.canResendSms ?? isSmsValid);
    const canResendAll =
      isAccountant ||
      (record?.canResendAll ?? (canResendEmail && canResendSms));

    return { canResendEmail, canResendSms, canResendAll };
  };

  const handleResend = async (
    record: any,
    channel: 'EMAIL' | 'SMS' | 'ALL',
  ) => {
    const { canResendEmail, canResendSms, canResendAll } =
      checkCanResend(record);

    if (!isAccountant) {
      if (channel === 'EMAIL' && !canResendEmail) {
        message.warning(
          record?.emailSuccessCount > 0
            ? 'Hóa đơn đã được gửi thành công qua Email. Không thể gửi lại.'
            : 'Kênh Email đã vượt quá số lần gửi lại tối đa (3 lần).',
        );
        return;
      }
      if (channel === 'SMS' && !canResendSms) {
        message.warning(
          record?.smsSuccessCount > 0
            ? 'Hóa đơn đã được gửi thành công qua SMS. Không thể gửi lại.'
            : 'Kênh SMS đã vượt quá số lần gửi lại tối đa (3 lần).',
        );
        return;
      }
      if (channel === 'ALL' && !canResendAll) {
        message.warning(
          'Không đủ điều kiện gửi lại tất cả các kênh (kênh đã thành công hoặc đã gửi tối đa 3 lần).',
        );
        return;
      }
    }

    const channels = channel === 'ALL' ? ['EMAIL', 'SMS'] : [channel];
    const channelLabel =
      channel === 'ALL' ? 'Email & SMS' : channel === 'EMAIL' ? 'Email' : 'SMS';
    try {
      setResending(true);
      const res: any = await EinvoiceHistoryApis.resendEinvoice({
        soCode: record.soCode || record.invoiceCode,
        channels: channels,
        invoiceCode: record.invoiceCode,
        billNum: record.billNum,
        branchCode: record.branchCode,
        lookupUrl: record.lookupUrl || '',
        customerId:
          record.customerId || customerInfo?.sapCustomerId || customerInfo?.id,
        customerName:
          record.customerName || customerInfo?.name || customerInfo?.storeName,
        customerType: record.customerType || customerInfo?.customerType,
        taxCode: record.taxCode || customerInfo?.taxId || customerInfo?.cccd,
        customerEmail: record.customerEmail || customerInfo?.email,
        customerPhone: record.customerPhone || customerInfo?.phone,
      });

      const isSuccess = res?.success || res?.data?.success;
      if (isSuccess) {
        message.success(
          res?.message ||
            res?.data?.message ||
            `Yêu cầu gửi lại qua ${channelLabel} đã được tiếp nhận!`,
        );
        fetchHistory(historyData.currentPage, historyData.pageSize);
      } else {
        message.error(
          res?.message ||
            res?.data?.message ||
            `Gửi lại qua ${channelLabel} thất bại`,
        );
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || error.message || 'Có lỗi xảy ra',
      );
    } finally {
      setResending(false);
    }
  };

  const getActionMenuItems = (record: any): MenuProps['items'] => {
    const { canResendEmail, canResendSms, canResendAll } =
      checkCanResend(record);

    return [
      {
        key: 'detail',
        label: 'Xem chi tiết',
        onClick: () => {
          setSelectedRecord(record);
          setIsOpenDetailModal(true);
        },
      },
      {
        key: 'resend_email',
        label: 'Gửi Email HĐ',
        disabled: !canResendEmail,
        onClick: () => handleResend(record, 'EMAIL'),
      },
      {
        key: 'resend_sms',
        label: 'Gửi SMS HĐ',
        disabled: !canResendSms,
        onClick: () => handleResend(record, 'SMS'),
      },
      {
        key: 'resend_all',
        label: 'Gửi lại tất cả',
        disabled: !canResendAll,
        onClick: () => handleResend(record, 'ALL'),
      },
    ];
  };

  const columns: ColumnsTypeCustom = [
    {
      title: 'Chi Nhánh',
      width: 100,
      dataIndex: 'branchCode',
      align: 'center',
      render: (val: string) => val || '-',
    },
    {
      title: 'Mã Bill',
      width: 130,
      dataIndex: 'billNum',
      render: (val: string, record: any) =>
        val ||
        record?.billNum ||
        record?.logs?.find((l: any) => l.billNum && l.billNum !== '-')
          ?.billNum ||
        '-',
    },
    {
      title: 'Số HĐ',
      width: 90,
      dataIndex: 'invoiceNumber',
      align: 'center',
      render: (val: string, record: any) =>
        val ||
        record?.logs?.find(
          (l: any) => l.invoiceNumber && l.invoiceNumber !== '-',
        )?.invoiceNumber ||
        '-',
    },
    {
      title: 'Ký Hiệu',
      width: 110,
      dataIndex: 'invoiceSerial',
      align: 'center',
      render: (val: string, record: any) =>
        val ||
        record?.logs?.find(
          (l: any) => l.invoiceSerial && l.invoiceSerial !== '-',
        )?.invoiceSerial ||
        '-',
    },
    {
      title: 'Ngày Phát Hành',
      width: 130,
      dataIndex: 'publishDate',
      align: 'center',
      render: (val: string, record: any) => {
        const d =
          val || record?.logs?.find((l: any) => l.publishDate)?.publishDate;
        return d ? dayjs(d).format('DD/MM/YYYY') : '-';
      },
    },
    {
      title: 'Giá Trị HĐ',
      width: 140,
      dataIndex: 'invoiceAmount',
      align: 'right',
      render: (val: number) => (val ? formatCurrency({ value: val }) : '0 đ'),
    },
    {
      title: 'Mã KH',
      width: 130,
      dataIndex: 'customerId',
      render: (val: string) => val || '-',
    },
    {
      title: 'Tên KH',
      width: 160,
      dataIndex: 'customerName',
      render: (val: string) => val || '-',
    },
    {
      title: 'Loại KH',
      width: 130,
      dataIndex: 'customerType',
      render: (val: string) => val || '-',
    },
    {
      title: 'Số Lần Gửi SMS Thành Công',
      width: 150,
      dataIndex: 'smsSuccessCount',
      align: 'center',
      render: (val: number) => val ?? 0,
    },
    {
      title: 'Số Lần Gửi Email Thành Công',
      width: 150,
      dataIndex: 'emailSuccessCount',
      align: 'center',
      render: (val: number) => val ?? 0,
    },
    {
      title: 'Ngày Gửi Thành Công',
      width: 140,
      dataIndex: 'successTime',
      align: 'center',
      render: (val: string) => (val ? dayjs(val).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Người Gửi',
      width: 140,
      dataIndex: 'successUser',
      render: (val: string, record: any) =>
        val ||
        (record?.smsSuccessCount > 0 || record?.emailSuccessCount > 0
          ? record?.userFullName
          : '-'),
    },
    {
      title: 'Số Lần Gửi SMS Thất Bại',
      width: 140,
      dataIndex: 'smsFailedCount',
      align: 'center',
      render: (val: number) => val ?? 0,
    },
    {
      title: 'Số Lần Gửi Email Thất Bại',
      width: 140,
      dataIndex: 'emailFailedCount',
      align: 'center',
      render: (val: number) => val ?? 0,
    },
    {
      title: 'Ngày Gửi Thất Bại',
      width: 140,
      dataIndex: 'failedTime',
      align: 'center',
      render: (val: string) => (val ? dayjs(val).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Người Gửi',
      width: 140,
      dataIndex: 'failedUser',
      render: (val: string, record: any) =>
        val ||
        (record?.emailFailedCount > 0 || record?.smsFailedCount > 0
          ? record?.userFullName
          : '-'),
    },
    {
      title: 'Tác Vụ',
      width: 80,
      align: 'center',
      fixed: 'right',
      render: (_, record: any) => (
        <Dropdown
          menu={{ items: getActionMenuItems(record) }}
          trigger={['click']}
        >
          <button className="cursor-pointer p-1 hover:bg-gray-100 rounded">
            <MoreOutlined className="text-lg rotate-90" />
          </button>
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingRight: 32,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <HistoryOutlined style={{ color: '#0052cc', fontSize: 20 }} />
              <span style={{ fontSize: 17, fontWeight: 600 }}>
                Lịch sử gửi HĐĐT - Bảng THTT: {sumPaymentId || ''}
              </span>
            </div>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                size="small"
                onClick={() =>
                  fetchHistory(historyData.currentPage, historyData.pageSize)
                }
                loading={loading}
              >
                Làm mới
              </Button>
            </Space>
          </div>
        }
        open={isOpen}
        onCancel={onClose}
        width="95vw"
        style={{ top: 20, maxWidth: '95vw' }}
        footer={[
          <Button key="close" type="primary" onClick={onClose}>
            Đóng
          </Button>,
        ]}
      >
        <Spin spinning={loading || resending} tip="Đang tải dữ liệu...">
          <div style={{ marginTop: 8 }}>
            <TableComponent
              columns={columns}
              dataSource={historyData.items || []}
              loading={loading || resending}
              bordered
              rowKey={(r: any) => r.soCode || r.invoiceCode || r.id}
              scroll={{ x: 2600 }}
              pagination={{
                current: historyData.currentPage,
                pageSize: historyData.pageSize,
                total: historyData.total,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50'],
                showTotal: (total) => `Tổng cộng ${total} bản ghi`,
                onChange: (page, pageSize) => {
                  fetchHistory(page, pageSize);
                },
              }}
            />
          </div>
        </Spin>
      </Modal>

      {/* Modal chi tiết logs */}
      {selectedRecord && (
        <DetailEinvoiceHistoryModal
          isOpen={isOpenDetailModal}
          onClose={() => {
            setIsOpenDetailModal(false);
            setSelectedRecord(null);
          }}
          record={selectedRecord}
        />
      )}
    </>
  );
};

export default PaymentSummaryEinvoiceHistoryModal;
